import DataTableBase from "./DataTableBase";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { CONFIG } from "../../config";

const AVAILABLE_SCOPES = [
  "read:establishments",
  "read:residences",
  "read:devices",
  "read:consumption",
  "write:occupancy"
];

const SCOPE_LABELS = {
  "read:establishments": "Lecture établissements",
  "read:residences": "Lecture résidences",
  "read:devices": "Lecture appareils",
  "read:consumption": "Lecture consommation",
  "write:occupancy": "Écriture occupation"
};

const RATE_LIMIT_TIERS = ["standard", "premium", "enterprise"];

const TIER_CONFIG = {
  standard: { label: "Standard", color: "#6b7280", bg: "rgba(107, 114, 128, 0.15)" },
  premium: { label: "Premium", color: "#3b82f6", bg: "rgba(59, 130, 246, 0.15)" },
  enterprise: { label: "Enterprise", color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.15)" }
};

export default function APIKeysTable() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // État pour le modal de modification
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    scopes: [],
    rate_limit_tier: "standard"
  });

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${CONFIG.API_URL}/admin/api-keys/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
      // Erreur silencieuse
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir révoquer la clé API "${name}" ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${CONFIG.API_URL}/admin/api-keys/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchApiKeys();
      } else {
        alert("Erreur lors de la révocation de la clé API");
      }
    } catch (error) {
      alert("Erreur lors de la révocation de la clé API");
    }
  };

  const handleOpenEdit = (apiKey) => {
    setEditingKey(apiKey);
    setEditForm({
      name: apiKey.name || "",
      description: apiKey.description || "",
      scopes: apiKey.scopes || [],
      rate_limit_tier: apiKey.rate_limit_tier || "standard"
    });
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditingKey(null);
  };

  const handleSaveEdit = async () => {
    if (!editingKey) return;

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${CONFIG.API_URL}/admin/api-keys/${editingKey.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editForm),
      });

      if (response.ok) {
        handleCloseEdit();
        fetchApiKeys();
      } else {
        const error = await response.json();
        alert(error.detail || "Erreur lors de la modification de la clé API");
      }
    } catch (error) {
      alert("Erreur lors de la modification de la clé API");
    }
  };

  const handlePermanentDelete = async (id, name) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir SUPPRIMER DÉFINITIVEMENT la clé API "${name}" ?\n\nCette action est irréversible et supprimera également tous les logs de requêtes associés.`)) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${CONFIG.API_URL}/admin/api-keys/${id}/permanent`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchApiKeys();
      } else {
        const error = await response.json();
        alert(error.detail || "Erreur lors de la suppression de la clé API");
      }
    } catch (error) {
      alert("Erreur lors de la suppression de la clé API");
    }
  };

  const handleScopeChange = (scope) => {
    setEditForm(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope]
    }));
  };

  // Formater la date avec heure relative
  const formatDate = (dateString) => {
    if (!dateString) return "Jamais";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return diffMins <= 1 ? "À l'instant" : `Il y a ${diffMins} min`;
      }
      return `Il y a ${diffHours}h`;
    } else if (diffDays === 1) {
      return "Hier";
    } else if (diffDays < 7) {
      return `Il y a ${diffDays} jours`;
    }
    return date.toLocaleDateString("fr-FR");
  };

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: "name",
      label: "Nom",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VpnKeyIcon sx={{ fontSize: 18, color: "#26A69A" }} />
            <Typography sx={{ fontWeight: 500 }}>{value}</Typography>
          </Box>
        ),
      },
    },
    {
      name: "establishment_name",
      label: "Établissement",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "environment",
      label: "Environnement",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const isLive = value === "live";
          return (
            <Chip
              label={isLive ? "PRODUCTION" : "TEST"}
              size="small"
              sx={{
                backgroundColor: isLive ? "rgba(34, 197, 94, 0.15)" : "rgba(251, 191, 36, 0.15)",
                color: isLive ? "#22c55e" : "#fbbf24",
                fontWeight: 600,
                fontSize: "0.7rem",
                border: `1px solid ${isLive ? "#22c55e" : "#fbbf24"}`,
              }}
            />
          );
        },
      },
    },
    {
      name: "scopes",
      label: "Permissions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          const readCount = value.filter(s => s.startsWith("read:")).length;
          const writeCount = value.filter(s => s.startsWith("write:")).length;
          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {readCount > 0 && (
                <Chip
                  label={`${readCount} lecture`}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(59, 130, 246, 0.15)",
                    color: "#3b82f6",
                    fontSize: "0.7rem",
                  }}
                />
              )}
              {writeCount > 0 && (
                <Chip
                  label={`${writeCount} écriture`}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(249, 115, 22, 0.15)",
                    color: "#f97316",
                    fontSize: "0.7rem",
                  }}
                />
              )}
            </Box>
          );
        },
      },
    },
    {
      name: "rate_limit_tier",
      label: "Niveau",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const config = TIER_CONFIG[value] || TIER_CONFIG.standard;
          return (
            <Chip
              label={config.label}
              size="small"
              sx={{
                backgroundColor: config.bg,
                color: config.color,
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            />
          );
        },
      },
    },
    {
      name: "is_active",
      label: "Statut",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {value ? (
                <>
                  <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} />
                  <Typography sx={{ color: "#22c55e", fontSize: "0.85rem", fontWeight: 500 }}>
                    Actif
                  </Typography>
                </>
              ) : (
                <>
                  <CancelIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                  <Typography sx={{ color: "#ef4444", fontSize: "0.85rem", fontWeight: 500 }}>
                    Révoqué
                  </Typography>
                </>
              )}
            </Box>
          );
        },
      },
    },
    {
      name: "last_used_at",
      label: "Dernière utilisation",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography sx={{ fontSize: "0.85rem", color: value ? "#9ca3af" : "#6b7280" }}>
            {formatDate(value)}
          </Typography>
        ),
      },
    },
    {
      name: "created_at",
      label: "Créé le",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography sx={{ fontSize: "0.85rem", color: "#9ca3af" }}>
            {new Date(value).toLocaleDateString("fr-FR")}
          </Typography>
        ),
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const apiKey = apiKeys.find(
            (key) => parseInt(key.id) === parseInt(tableMeta.rowData[0])
          );

          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Voir détails">
                <IconButton
                  size="small"
                  sx={{ color: "#26A69A" }}
                  onClick={() => navigate(`/api-keys/${apiKey.id}`)}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {apiKey.is_active && (
                <>
                  <Tooltip title="Modifier">
                    <IconButton
                      size="small"
                      sx={{ color: "#3b82f6" }}
                      onClick={() => handleOpenEdit(apiKey)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Révoquer">
                    <IconButton
                      size="small"
                      sx={{ color: "#f97316" }}
                      onClick={() => handleRevoke(apiKey.id, apiKey.name)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {!apiKey.is_active && (
                <Tooltip title="Supprimer définitivement">
                  <IconButton
                    size="small"
                    sx={{ color: "#ef4444" }}
                    onClick={() => handlePermanentDelete(apiKey.id, apiKey.name)}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    },
  ];

  return (
    <>
      <DataTableBase
        title="Clés API"
        data={apiKeys}
        columns={columns}
        loading={loading}
      />

      {/* Modal de modification */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(40, 50, 45, 0.98)",
            backgroundImage: "none",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "12px",
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
          <VpnKeyIcon sx={{ color: "#26A69A" }} />
          <span>Modifier la clé API</span>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <TextField
              label="Nom"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                  "&:hover fieldset": { borderColor: "#26A69A" },
                  "&.Mui-focused fieldset": { borderColor: "#26A69A" },
                }
              }}
            />
            <TextField
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                  "&:hover fieldset": { borderColor: "#26A69A" },
                  "&.Mui-focused fieldset": { borderColor: "#26A69A" },
                }
              }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ "&.Mui-focused": { color: "#26A69A" } }}>Niveau de quota</InputLabel>
              <Select
                value={editForm.rate_limit_tier}
                label="Niveau de quota"
                onChange={(e) => setEditForm({ ...editForm, rate_limit_tier: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#26A69A" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#26A69A" },
                }}
              >
                {RATE_LIMIT_TIERS.map((tier) => {
                  const config = TIER_CONFIG[tier];
                  return (
                    <MenuItem key={tier} value={tier}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: config.color
                        }} />
                        {config.label}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Box>
              <Typography sx={{ mb: 1, color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>
                Permissions
              </Typography>
              <Box sx={{
                backgroundColor: "rgba(0,0,0,0.2)",
                borderRadius: "8px",
                p: 1.5,
                border: "1px solid rgba(255,255,255,0.1)"
              }}>
                <FormGroup>
                  {AVAILABLE_SCOPES.map((scope) => (
                    <FormControlLabel
                      key={scope}
                      control={
                        <Checkbox
                          checked={editForm.scopes.includes(scope)}
                          onChange={() => handleScopeChange(scope)}
                          sx={{
                            color: "rgba(255,255,255,0.3)",
                            "&.Mui-checked": { color: "#26A69A" },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={scope.startsWith("read:") ? "LECTURE" : "ÉCRITURE"}
                            size="small"
                            sx={{
                              fontSize: "0.65rem",
                              height: "18px",
                              backgroundColor: scope.startsWith("read:")
                                ? "rgba(59, 130, 246, 0.15)"
                                : "rgba(249, 115, 22, 0.15)",
                              color: scope.startsWith("read:") ? "#3b82f6" : "#f97316",
                            }}
                          />
                          <span style={{ fontSize: "0.9rem" }}>{SCOPE_LABELS[scope]}</span>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid rgba(255,255,255,0.1)", p: 2 }}>
          <Button
            onClick={handleCloseEdit}
            sx={{ color: "rgba(255,255,255,0.7)" }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={{
              backgroundColor: "#26A69A",
              "&:hover": { backgroundColor: "#00897B" }
            }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
